import {
	Checkbox as AntCheckbox,
	Col,
	Collapse,
	Divider,
	Form,
	Input,
	Radio,
	Row,
	Space,
	Table,
	Tag,
	Typography,
} from 'antd';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import type { FieldConfig } from './QCAccordionForm';
import { buildRows, renderRows } from './QCAccordionForm';
import { useQCPraPencairanData } from '@/pages/robotic/qc-pra-pencairan/QCPraPencairanContext';
import { applyFieldMeta } from '@/pages/robotic/qc-pra-pencairan/qc-pd-mapping';

// ─── Types ───────────────────────────────────────────────────────────────────

type TipeAsuransiType = 'kombinasi' | 'banker-clause';
type PembayaranType = 'full-cash' | 'kombinasi-cash-onloan' | 'full-cash-pdc';

// ─── Options ─────────────────────────────────────────────────────────────────

const TIPE_APLIKASI_OPTIONS = [
	{ label: 'Non Group', value: 'Non Group' },
	{ label: 'Repeat Order', value: 'Repeat Order' },
];

const YA_TIDAK_OPTIONS = [
	{ label: 'Ya', value: 'yes' },
	{ label: 'Tidak', value: 'no' },
];

const CONDITION_LABELS: Record<number, string> = {
	1: 'Kombinasi / Banker Clause Dibayar Debitur / Simulasi Diskon',
	2: 'Banker Clause MTF - Pembayaran Full Cash',
	3: 'Banker Clause MTF - Kombinasi (Cash + Onloan)',
	4: 'Banker Clause MTF - Full Cash Menggunakan PDC',
};

// ─── Table Data ──────────────────────────────────────────────────────────────

type InsuranceTableRow = {
	key: number;
	tahun: number;
	dibayarCustomer: string;
	paidAmount: string;
};

const TABLE_DATA: Record<number, InsuranceTableRow[]> = {
	2: [
		{ key: 1, tahun: 1, dibayarCustomer: 'Paid', paidAmount: 'Rp 5.000.000' },
		{ key: 2, tahun: 2, dibayarCustomer: 'Paid', paidAmount: 'Rp 3.000.000' },
		{ key: 3, tahun: 3, dibayarCustomer: 'Paid', paidAmount: 'Rp 3.000.000' },
	],
	3: [
		{ key: 1, tahun: 1, dibayarCustomer: 'Paid', paidAmount: 'Rp 5.000.000' },
		{ key: 2, tahun: 2, dibayarCustomer: 'Paid', paidAmount: 'Rp 0' },
		{ key: 3, tahun: 3, dibayarCustomer: 'Paid', paidAmount: 'Rp 0' },
	],
	4: [
		{ key: 1, tahun: 1, dibayarCustomer: 'Paid', paidAmount: 'Rp 5.000.000' },
		{ key: 2, tahun: 2, dibayarCustomer: 'PDC', paidAmount: 'Rp 0' },
		{ key: 3, tahun: 3, dibayarCustomer: 'PDC', paidAmount: 'Rp 0' },
		{ key: 4, tahun: 4, dibayarCustomer: 'PDC', paidAmount: 'Rp 0' },
	],
};

const TABLE_COLUMNS = [
	{ title: 'Tahun', dataIndex: 'tahun', key: 'tahun', width: 80 },
	{ title: 'Dibayar Customer', dataIndex: 'dibayarCustomer', key: 'dibayarCustomer' },
	{ title: 'Paid Amount by Customer', dataIndex: 'paidAmount', key: 'paidAmount' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const renderGroup = (fields: FieldConfig[], readOnly: boolean) =>
	renderRows(buildRows(fields), readOnly);

// Common insurance detail fields shared across conditions 2-4
const COMMON_ENTRY_FIELDS: FieldConfig[] = [
	{ name: 'insTjh', label: 'TJH', type: 'currency', disabled: true, required: true },
	{ name: 'insHuruHara', label: 'Huru Hara', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insBanjir', label: 'Banjir', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insGempa', label: 'Gempa', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insTeroris', label: 'Teroris', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insPaPengemudi', label: 'PA Pengemudi', type: 'currency', disabled: true, required: true },
	{ name: 'insJumlahPenumpang', label: 'Jumlah Penumpang', type: 'number', disabled: true, required: true },
	{ name: 'insAsuransiGap', label: 'Asuransi GAP', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insAsuransiHospitalInCar', label: 'Asuransi Hospital In Car', type: 'radio', required: true, options: YA_TIDAK_OPTIONS },
	{ name: 'insDiscountAsuransi', label: 'Discount Asuransi', type: 'currency', disabled: true, required: true },
	{ name: 'insSubsidiAsuransiPihakKetiga', label: 'Subsidi Asuransi Pihak Ketiga', type: 'currency', disabled: true, required: true },
	{ name: 'insBiayaAdminPokokHutang', label: 'Biaya Admin Yang Dimasukkan Pokok Hutang', type: 'currency', disabled: true, required: false },
	{ name: 'insBiayaFiduciaPokokHutang', label: 'Biaya Fiducia Yang Dibayarkan Pokok Hutang', type: 'currency', disabled: true, required: false },
	{ name: 'insBiayaProvisiPokokHutang', label: 'Biaya Provisi Yang Dibayarkan Pokok Hutang', type: 'currency', disabled: true, required: false },
];

// ─── Component ───────────────────────────────────────────────────────────────

const QCInsuranceDataForm: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const { insurance: insData, fieldMeta } = useQCPraPencairanData();
	const form = Form.useFormInstance();
	const withMeta = (fields: FieldConfig[]) => applyFieldMeta(fields, fieldMeta);
	const [tipeAsuransi, setTipeAsuransi] = useState<TipeAsuransiType>('kombinasi');
	const [pembayaran, setPembayaran] = useState<PembayaranType>('full-cash');

	// Sync condition from BE-populated form values. BE drives the insurance type label
	// (e.g. "Banker Clause MTF", "Kombinasi") and the payment label
	// (e.g. "Full Cash", "Kombinasi (Cash + Onloan)", "Full Cash + PDC").
	const insTipeAsuransiValue = Form.useWatch('insTipeAsuransi', form) as string | undefined;
	const insPembayaranAsuransiValue = Form.useWatch('insPembayaranAsuransi', form) as string | undefined;

	useEffect(() => {
		if (!insTipeAsuransiValue) return;
		const tipe = insTipeAsuransiValue.toLowerCase();
		setTipeAsuransi(tipe.includes('banker') ? 'banker-clause' : 'kombinasi');
	}, [insTipeAsuransiValue]);

	useEffect(() => {
		if (!insPembayaranAsuransiValue) return;
		const pay = insPembayaranAsuransiValue.toLowerCase();
		if (pay.includes('pdc')) {
			setPembayaran('full-cash-pdc');
		} else if (pay.includes('kombinasi') || pay.includes('onloan')) {
			setPembayaran('kombinasi-cash-onloan');
		} else {
			setPembayaran('full-cash');
		}
	}, [insPembayaranAsuransiValue]);

	const conditionNumber = (() => {
		if (tipeAsuransi === 'kombinasi') return 1;
		if (pembayaran === 'full-cash') return 2;
		if (pembayaran === 'kombinasi-cash-onloan') return 3;
		return 4;
	})();

	const showPilihPembayaran = conditionNumber === 2 || conditionNumber === 4;
	const showSkemaPembayaran = conditionNumber === 4;
	const showPilihTenor = conditionNumber === 4;

	// ─── Condition 1: Flat fields ────────────────────────────────────────────

	const renderCondition1 = () => (
		<>
			{renderGroup(withMeta([
				{ name: 'insDiasuransikanOleh', label: 'Diasuransikan Oleh', type: 'text', disabled: true, required: true },
				{ name: 'insDibayarkanOleh', label: 'Dibayarkan Oleh', type: 'text', disabled: true, required: true },
				{ name: 'insTenorCredit', label: 'Tenor Credit', type: 'number', disabled: true, required: true },
				{
					name: 'insTipeAplikasi', label: 'Tipe Aplikasi', type: 'select', required: true,
					options: TIPE_APLIKASI_OPTIONS,
				},
				{ name: 'insNilaiPertanggungan', label: 'Nilai Pertanggungan', type: 'text', disabled: true, required: true },
				{ name: 'insAsuransiTenor', label: 'Asuransi Tenor', type: 'text', disabled: true, required: true },
			]), readOnly)}

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name="insTipeAsuransi"
						label="Tipe Asuransi"
						required={fieldMeta['insTipeAsuransi']?.required ?? true}
						labelCol={{ span: 8 }}
						wrapperCol={{ span: 16 }}
					>
						<Input disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						label="Dokumen Polis Asuransi"
						required
						labelCol={{ span: 8 }}
						wrapperCol={{ span: 16 }}
					>
						{insData.policyDocumentUrl ? (
							<a href={insData.policyDocumentUrl} target="_blank" rel="noopener noreferrer">View Dokumen</a>
						) : (
							<span style={{ color: '#bababa' }}>View Dokumen</span>
						)}
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						label="Bukti Pembayaran Asuransi"
						required
						labelCol={{ span: 8 }}
						wrapperCol={{ span: 16 }}
					>
						{insData.paymentDocumentUrl ? (
							<a href={insData.paymentDocumentUrl} target="_blank" rel="noopener noreferrer">View Dokumen</a>
						) : (
							<span style={{ color: '#bababa' }}>View Dokumen</span>
						)}
					</Form.Item>
				</Col>
			</Row>
		</>
	);

	// ─── Conditions 2-4: Top fields ──────────────────────────────────────────

	const renderTopFields = () =>
		renderGroup(withMeta([
			{ name: 'insWilayahAsuransi', label: 'Wilayah Asuransi', type: 'text', disabled: true, required: true },
			{ name: 'insDiasuransikanOleh', label: 'Diasuransikan Oleh', type: 'text', disabled: true, required: true },
			{ name: 'insDibayarkanOleh', label: 'Dibayarkan Oleh', type: 'text', disabled: true, required: true },
			{ name: 'insTenorCredit', label: 'Tenor Credit', type: 'number', disabled: true, required: true },
			{
				name: 'insTipeAplikasi', label: 'Tipe Aplikasi', type: 'select', required: true,
				options: TIPE_APLIKASI_OPTIONS,
			},
			{ name: 'insNilaiPertanggungan', label: 'Nilai Pertanggungan', type: 'text', disabled: true, required: true },
			{ name: 'insRateJual', label: 'Rate Jual', type: 'text', disabled: true, required: true },
			{ name: 'insRateJualAcp', label: 'Rate Jual ACP', type: 'text', disabled: true, required: true },
		]), readOnly);

	// ─── Insurance Entry accordion content ───────────────────────────────────

	const renderInsuranceEntry = () => {
		const headerFields: FieldConfig[] = withMeta([
			{ name: 'insTipeAsuransi', label: 'Tipe Asuransi', type: 'text', disabled: true, required: true },
			{ name: 'insPembayaranAsuransi', label: 'Pembayaran Asuransi', type: 'text', disabled: true, required: true },
			{ name: 'insAsuransiTenor', label: 'Asuransi Tenor', type: 'text', disabled: true, required: true },
			...(showPilihPembayaran
				? [{ name: 'insPilihPembayaran', label: 'Pilih Pembayaran', type: 'text' as const, disabled: true, required: true }]
				: []),
		]);

		return (
			<>
				{renderGroup(headerFields, readOnly)}

				{showSkemaPembayaran && (
					<>
						<Divider />
						{renderGroup(withMeta([
							{ name: 'insSkemaPembayaran', label: 'Skema Pembayaran', type: 'text', disabled: true, required: true },
						]), readOnly)}
					</>
				)}

				{showPilihTenor && (
					<>
						<Divider />
						<Row gutter={[40, 20]}>
							<Col span={24}>
								<Form.Item
									name="insPilihTenor"
									label="Pilih Tenor"
									required
									labelCol={{ span: 4 }}
									wrapperCol={{ span: 20 }}
								>
									<AntCheckbox.Group disabled={readOnly}>
										<AntCheckbox value="1">Tahun Ke-1</AntCheckbox>
										<AntCheckbox value="2">Tahun Ke-2</AntCheckbox>
										<AntCheckbox value="3">Tahun Ke-3</AntCheckbox>
										<AntCheckbox value="4">Tahun Ke-4</AntCheckbox>
									</AntCheckbox.Group>
								</Form.Item>
							</Col>
						</Row>
					</>
				)}

				<Divider />
				{renderGroup(withMeta(COMMON_ENTRY_FIELDS), readOnly)}
			</>
		);
	};

	// ─── Render ──────────────────────────────────────────────────────────────

	return (
		<>
			{__IS_DEVELOPMENT__ && (
				<div style={{
					marginBottom: 16,
					padding: 16,
					background: '#f0f5ff',
					borderRadius: 8,
					border: '1px dashed #1557BC',
				}}>
					<Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
						[DEV] Override Kondisi Insurance Data
					</Typography.Text>
					<Space direction="vertical" size={8} style={{ width: '100%' }}>
						<Space wrap>
							<Typography.Text type="secondary">Tipe Asuransi:</Typography.Text>
							<Radio.Group
								value={tipeAsuransi}
								onChange={(e) => setTipeAsuransi(e.target.value)}
								optionType="button"
								buttonStyle="solid"
								size="small"
							>
								<Radio.Button value="kombinasi">Kombinasi / Debitur</Radio.Button>
								<Radio.Button value="banker-clause">Banker Clause - MTF</Radio.Button>
							</Radio.Group>
						</Space>
						{tipeAsuransi === 'banker-clause' && (
							<Space wrap>
								<Typography.Text type="secondary">Pembayaran Asuransi:</Typography.Text>
								<Radio.Group
									value={pembayaran}
									onChange={(e) => setPembayaran(e.target.value)}
									optionType="button"
									buttonStyle="solid"
									size="small"
								>
									<Radio.Button value="full-cash">Full Cash</Radio.Button>
									<Radio.Button value="kombinasi-cash-onloan">Kombinasi (Cash + Onloan)</Radio.Button>
									<Radio.Button value="full-cash-pdc">Full Cash + PDC</Radio.Button>
								</Radio.Group>
							</Space>
						)}
						<Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>
							Kondisi {conditionNumber}: {CONDITION_LABELS[conditionNumber]}
						</Tag>
					</Space>
				</div>
			)}

			{conditionNumber === 1 ? (
				renderCondition1()
			) : (
				<>
					{renderTopFields()}

					<Collapse
						style={{ marginTop: 16 }}
						items={[{
							key: 'insurance-entry',
							label: 'Klik OK - Insurance Entry',
							children: renderInsuranceEntry(),
						}]}
						defaultActiveKey={['insurance-entry']}
					/>

					<Table<InsuranceTableRow>
						bordered
						pagination={false}
						style={{ marginTop: 16 }}
						dataSource={
							insData.paymentSchedule.length > 0
								? insData.paymentSchedule.map((item, idx) => ({
									key: idx + 1,
									tahun: item.year,
									dibayarCustomer: item.paidByCustomer ?? '-',
									paidAmount: `Rp ${(item.paidAmountByCustomer ?? 0).toLocaleString('id-ID')}`,
								}))
								: TABLE_DATA[conditionNumber]
						}
						columns={TABLE_COLUMNS}
					/>
				</>
			)}
		</>
	);
};

export default QCInsuranceDataForm;
